/**
 * Generated by orval v7.10.0 🍺
 * Do not edit manually.
 * Fintecy Investors API
 * API for managing investors, accounts, and transactions in the Fintecy platform
 * OpenAPI spec version: 1.0.0
 */
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  MutationFunction,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import { useMutation, useQuery } from '@tanstack/react-query';

import type { Error, ProfileSettings, TaxSettings, TaxSettingsInput } from '.././model';

import { customInstance } from '../../axios-instance';

/**
 * Returns the user's profile settings including personal info, preferences, and security settings
 * @summary Get profile settings
 */
export const getProfile = (signal?: AbortSignal) => {
  return customInstance<ProfileSettings>({ url: `/api/settings`, method: 'GET', signal });
};

export const getGetProfileQueryKey = () => {
  return [`/api/settings`] as const;
};

export const getGetProfileQueryOptions = <
  TData = Awaited<ReturnType<typeof getProfile>>,
  TError = Error,
>(options?: {
  query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getProfile>>, TError, TData>>;
}) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetProfileQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getProfile>>> = ({ signal }) =>
    getProfile(signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getProfile>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetProfileQueryResult = NonNullable<Awaited<ReturnType<typeof getProfile>>>;
export type GetProfileQueryError = Error;

export function useGetProfile<TData = Awaited<ReturnType<typeof getProfile>>, TError = Error>(
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getProfile>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getProfile>>,
          TError,
          Awaited<ReturnType<typeof getProfile>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetProfile<TData = Awaited<ReturnType<typeof getProfile>>, TError = Error>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getProfile>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getProfile>>,
          TError,
          Awaited<ReturnType<typeof getProfile>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetProfile<TData = Awaited<ReturnType<typeof getProfile>>, TError = Error>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getProfile>>, TError, TData>>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get profile settings
 */

export function useGetProfile<TData = Awaited<ReturnType<typeof getProfile>>, TError = Error>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getProfile>>, TError, TData>>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetProfileQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Updates the user's profile settings including personal info, preferences, and security settings
 * @summary Update profile settings
 */
export const updateProfile = (profileSettings: ProfileSettings) => {
  return customInstance<ProfileSettings>({
    url: `/api/settings`,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    data: profileSettings,
  });
};

export const getUpdateProfileMutationOptions = <TError = Error, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateProfile>>,
    TError,
    { data: ProfileSettings },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof updateProfile>>,
  TError,
  { data: ProfileSettings },
  TContext
> => {
  const mutationKey = ['updateProfile'];
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof updateProfile>>,
    { data: ProfileSettings }
  > = (props) => {
    const { data } = props ?? {};

    return updateProfile(data);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdateProfileMutationResult = NonNullable<Awaited<ReturnType<typeof updateProfile>>>;
export type UpdateProfileMutationBody = ProfileSettings;
export type UpdateProfileMutationError = Error;

/**
 * @summary Update profile settings
 */
export const useUpdateProfile = <TError = Error, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof updateProfile>>,
      TError,
      { data: ProfileSettings },
      TContext
    >;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof updateProfile>>,
  TError,
  { data: ProfileSettings },
  TContext
> => {
  const mutationOptions = getUpdateProfileMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
/**
 * Permanently erases the user account and all associated data (GDPR Article 17) — accounts, transactions, holdings, orders, goals, financial plans, estate structure, documents (including stored files), sessions, and credentials. This is irreversible.
 * @summary Delete user account
 */
export const deleteProfile = () => {
  return customInstance<void>({ url: `/api/settings`, method: 'DELETE' });
};

export const getDeleteProfileMutationOptions = <TError = Error, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<Awaited<ReturnType<typeof deleteProfile>>, TError, void, TContext>;
}): UseMutationOptions<Awaited<ReturnType<typeof deleteProfile>>, TError, void, TContext> => {
  const mutationKey = ['deleteProfile'];
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteProfile>>, void> = () => {
    return deleteProfile();
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteProfileMutationResult = NonNullable<Awaited<ReturnType<typeof deleteProfile>>>;

export type DeleteProfileMutationError = Error;

/**
 * @summary Delete user account
 */
export const useDeleteProfile = <TError = Error, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof deleteProfile>>,
      TError,
      void,
      TContext
    >;
  },
  queryClient?: QueryClient
): UseMutationResult<Awaited<ReturnType<typeof deleteProfile>>, TError, void, TContext> => {
  const mutationOptions = getDeleteProfileMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
/**
 * Returns jurisdiction-agnostic tax settings for the authenticated user
 * @summary Get tax settings
 */
export const getTaxSettings = (signal?: AbortSignal) => {
  return customInstance<TaxSettings>({ url: `/api/settings/tax`, method: 'GET', signal });
};

export const getGetTaxSettingsQueryKey = () => {
  return [`/api/settings/tax`] as const;
};

export const getGetTaxSettingsQueryOptions = <
  TData = Awaited<ReturnType<typeof getTaxSettings>>,
  TError = Error,
>(options?: {
  query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getTaxSettings>>, TError, TData>>;
}) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetTaxSettingsQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getTaxSettings>>> = ({ signal }) =>
    getTaxSettings(signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getTaxSettings>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetTaxSettingsQueryResult = NonNullable<Awaited<ReturnType<typeof getTaxSettings>>>;
export type GetTaxSettingsQueryError = Error;

export function useGetTaxSettings<
  TData = Awaited<ReturnType<typeof getTaxSettings>>,
  TError = Error,
>(
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getTaxSettings>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getTaxSettings>>,
          TError,
          Awaited<ReturnType<typeof getTaxSettings>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetTaxSettings<
  TData = Awaited<ReturnType<typeof getTaxSettings>>,
  TError = Error,
>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getTaxSettings>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getTaxSettings>>,
          TError,
          Awaited<ReturnType<typeof getTaxSettings>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetTaxSettings<
  TData = Awaited<ReturnType<typeof getTaxSettings>>,
  TError = Error,
>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getTaxSettings>>, TError, TData>>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get tax settings
 */

export function useGetTaxSettings<
  TData = Awaited<ReturnType<typeof getTaxSettings>>,
  TError = Error,
>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getTaxSettings>>, TError, TData>>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetTaxSettingsQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Partially updates tax settings for the authenticated user
 * @summary Update tax settings
 */
export const updateTaxSettings = (taxSettingsInput: TaxSettingsInput) => {
  return customInstance<TaxSettings>({
    url: `/api/settings/tax`,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    data: taxSettingsInput,
  });
};

export const getUpdateTaxSettingsMutationOptions = <TError = Error, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateTaxSettings>>,
    TError,
    { data: TaxSettingsInput },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof updateTaxSettings>>,
  TError,
  { data: TaxSettingsInput },
  TContext
> => {
  const mutationKey = ['updateTaxSettings'];
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof updateTaxSettings>>,
    { data: TaxSettingsInput }
  > = (props) => {
    const { data } = props ?? {};

    return updateTaxSettings(data);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdateTaxSettingsMutationResult = NonNullable<
  Awaited<ReturnType<typeof updateTaxSettings>>
>;
export type UpdateTaxSettingsMutationBody = TaxSettingsInput;
export type UpdateTaxSettingsMutationError = Error;

/**
 * @summary Update tax settings
 */
export const useUpdateTaxSettings = <TError = Error, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof updateTaxSettings>>,
      TError,
      { data: TaxSettingsInput },
      TContext
    >;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof updateTaxSettings>>,
  TError,
  { data: TaxSettingsInput },
  TContext
> => {
  const mutationOptions = getUpdateTaxSettingsMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
